<template>
  <div class="carousel">
    <div :class="twMerge('title flex items-center justify-between gap-3', props.titleContainerClass)">
      <slot name="title">
        <component :is="props.titleTag" :class="twMerge('text-lg font-500 my-4 truncate md:text-2xl md:my-3', props.titleClass)">
          {{ props.title }}
        </component>
      </slot>
      <client-only>
        <div class="actions h-content flex order-last items-center gap-3">
          <etg-button
            v-show="props.actionButton"
            class="my-3"
            :class="[(!isNavigatorShown || swiper?.isLocked || !appStore.isDesktopScreen) && '-mr-3']"
            type="text"
            size="small"
            @click="$emit('clickActionButton')"
          >
            {{ props.actionButtonText }}
          </etg-button>
          <div v-show="isNavigatorShown && appStore.isDesktopScreen && !swiper?.isLocked" class="flex gap-3 -mr-1.5">
            <etg-button
              :id="prevBtnId"
              type="icon"
              size="small"
              icon="arrow-left"
              :disabled="isPrevBtnDisabled"
              :aria-label="$t('Notification_previousPhoto')"
              @click="slidePrev"
            />
            <etg-button
              :id="nextBtnId"
              type="icon"
              size="small"
              icon="arrow-right"
              :disabled="isNextBtnDisabled"
              :aria-label="$t('Notification_nextPhoto')"
              @click="slideNext"
            />
          </div>
        </div>
      </client-only>
    </div>
    <etg-swiper
      ref="etgSwiperRef"
      :data="props.data"
      :autoplay="props.autoplay"
      :swiper-options="swiperOptions"
      :swiper-container-class="twMerge('-mx-5 sm:mx-0', props.swiperContainerClass)"
      :swiper-slide-class="twMerge('overflow-hidden first:ml-5 last:mr-5 sm:first:ml-0 sm:last:mr-0', props.swiperSlideClass)"
      @slide-change="$emit('slideChange')"
    >
      <template #default="{ item, index }">
        <slot :item="item" :index="index">
          <div class="slider-wrapper max-w-100% w-360px h-135px">
            <div class="w-full h-full flex justify-center items-center bg-gray-30 border rounded flex-0">Slide {{ item }}</div>
          </div>
        </slot>
      </template>
      <template #skeleton>
        <slot name="skeleton">
          <div class="skeleton-wrapper flex gap-4 py-4 overflow-hidden">
            <div v-for="i in 4" :key="i" class="skeleton-card">
              <n-skeleton class="w-360px h-135px rounded" />
              <n-skeleton class="mt-2 rounded" text />
              <n-skeleton class="mt-2 rounded w-60%" text />
            </div>
          </div>
        </slot>
      </template>
    </etg-swiper>
  </div>
</template>

<script setup lang="ts" generic="T">
import { NSkeleton } from 'naive-ui'
import type { SwiperOptions } from 'swiper/types'
import { twMerge } from 'tailwind-merge'

import type { EtgSwiperExposed } from '@/types/component'
import { useAppStore } from '@/store/app'
import { getRandomString } from '@/utils/common'
import EtgSwiper from '@/components/common/etg-swiper.vue'

interface Props {
  title?: string
  titleContainerClass?: string
  swiperContainerClass?: string
  swiperSlideClass?: string
  titleClass?: string
  titleTag?: string
  data?: readonly T[]
  actionButton?: boolean
  actionButtonText?: string
  navigator?: boolean
  autoplay?: boolean
  loop?: boolean
  gap?: number
  swiperOptions?: SwiperOptions
}

defineEmits(['clickActionButton', 'slideChange'])

const props = withDefaults(defineProps<Props>(), {
  data: () => [1, 2, 3, 4, 5, 6, 7, 8, 9, 10] as T[],
  title: '',
  titleTag: 'h2',
  actionButton: false,
  actionButtonText: () => 'Check more',
  navigator: false,
  autoplay: false,
  loop: false,
})

const appStore = useAppStore()
const etgSwiperRef = ref<Nullable<EtgSwiperExposed>>(null)

const prevBtnId = `prev-btn-${getRandomString()}`
const nextBtnId = `next-btn-${getRandomString()}`

const gap = computed(() => {
  if (typeof props.gap === 'number') return props.gap
  return appStore.isDesktopScreen ? 20 : 8
})
const isNavigatorShown = computed(() => props.navigator)

const swiperOptions = computed<SwiperOptions>(() => ({
  slidesPerView: 'auto',
  slidesPerGroup: 1,
  spaceBetween: gap.value,
  centeredSlides: false,
  pagination: false,
  speed: 300,
  cssMode: true,
  observer: true,
  freeMode: true,
  autoplay: props.autoplay && { delay: 3000 },
  rewind: props.loop && props.data.length > 1,
  ...props.swiperOptions,
}))

const swiper = computed(() => etgSwiperRef.value?.swiper)
const isBeginning = computed(() => etgSwiperRef.value?.isBeginning)
const isEnd = computed(() => etgSwiperRef.value?.isEnd)

const isPrevBtnDisabled = computed(() => !swiperOptions.value.rewind && isBeginning.value)
const isNextBtnDisabled = computed(() => !swiperOptions.value.rewind && isEnd.value)

defineExpose({ swiper })

const slidePrev = () => {
  swiper.value?.slidePrev()
}

const slideNext = () => {
  swiper.value?.slideNext()
}
</script>

<style lang="scss" scoped>
:deep(swiper-slide) {
  @apply w-fit;
}
</style>
